import html2canvas from "html2canvas";

const RENDER_SCALE = 2; // render 2× for sharpness, then downscale back

/**
 * Downscales a 2× canvas back to logical (1×) dimensions using OffscreenCanvas.
 * This gives you sharp pixels at the expected resolution.
 */
async function downscaleCanvas(hiResCanvas, scale) {
  const logicalW = hiResCanvas.width / scale;
  const logicalH = hiResCanvas.height / scale;

  const offscreen = new OffscreenCanvas(logicalW, logicalH);
  const ctx = offscreen.getContext("2d");

  // crisp-edges prevents the browser from blurring during drawImage
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(hiResCanvas, 0, 0, logicalW, logicalH);
  return offscreen.convertToBlob({ type: "image/png" });
}

/**
 * Captures a single DOM element as a sharp PNG Blob at logical resolution.
 */
export async function captureElementAsPng(element, options = {}) {
  const { scale = RENDER_SCALE, backgroundColor = null } = options;

  // Wait for all fonts to be loaded — prevents invisible/fallback text
  await document.fonts.ready;

  const canvas = await html2canvas(element, {
    backgroundColor,
    scale,             // render at 2× density for sharpness
    useCORS: true,
    allowTaint: false,
    logging: false,
    letterRendering: true,
    // Force html2canvas to use the full pixel dimensions
    width: element.offsetWidth,
    height: element.offsetHeight,
    scrollX: 0,
    scrollY: 0,
  });

  // Downscale back to logical (1×) dimensions so server gets expected size
  const blob = await downscaleCanvas(canvas, scale);

  return {
    blob,
    width: canvas.width / scale,    // logical width the server expects
    height: canvas.height / scale,
    cssWidth: canvas.width / scale,
    cssHeight: canvas.height / scale,
  };
}

/**
 * Captures every .blockWrapper element and returns ordered PNG blobs.
 */
export async function generateBlockImages(options = {}) {
  const { scale = RENDER_SCALE, onProgress } = options;

  const blocks = document.querySelectorAll(".blockWrapper");
  if (!blocks.length) throw new Error("No .blockWrapper elements found");

  const results = [];

  for (let idx = 0; idx < blocks.length; idx++) {
    const item = blocks[idx];

    const { blob, width, height, cssWidth, cssHeight } =
      await captureElementAsPng(item, { scale });

    results.push({ blob, idx, width, height, cssWidth, cssHeight });
    onProgress?.(idx + 1, blocks.length);
  }

  return results;
}

/**
 * Stitches multiple PNG blobs into a single vertical strip.
 */
export async function generateStitchedImage(blockImages) {
  const bitmaps = await Promise.all(
    blockImages.map(({ blob }) => createImageBitmap(blob))
  );

  const totalWidth = Math.max(...bitmaps.map((b) => b.width));
  const totalHeight = bitmaps.reduce((sum, b) => sum + b.height, 0);

  const offscreen = new OffscreenCanvas(totalWidth, totalHeight);
  const ctx = offscreen.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, totalWidth, totalHeight);

  let y = 0;
  for (const bitmap of bitmaps) {
    ctx.drawImage(bitmap, 0, y);
    y += bitmap.height;
    bitmap.close();
  }

  return offscreen.convertToBlob({ type: "image/png" });
}

export async function generateScrollPng(options = {}) {
  const blockImages = await generateBlockImages(options);
  return generateStitchedImage(blockImages);
}